import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { 
  FileText, 
  Download, 
  Printer,
  ArrowLeft,
  Package,
  Loader2,
  Hash,
  Calendar,
  Ruler,
  Scissors,
  CheckCircle,
  Clock,
  AlertCircle,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { workOrderApiService, WorkOrder } from '../services/workOrderApi';
import { endgoodApiService, Endgood } from '../services/endgoodApi';
import { materialIssueApiService } from '../services/materialIssueApi';
import { workOrderVariantBreakdownApiService } from '../services/workOrderVariantBreakdownApi';
import { fileUploadApiService } from '../services/fileUploadApi';

// Component to display variant thumbnails with pre-loaded images
const VariantThumbnail: React.FC<{ variantName: string; thumbnailUrl?: string }> = ({ variantName, thumbnailUrl }) => {
  if (!thumbnailUrl) {
    return (
      <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center">
        <Package className="h-4 w-4 text-gray-400" />
      </div>
    );
  }

  return (
    <img
      src={thumbnailUrl}
      alt={`${variantName} thumbnail`}
      className="w-8 h-8 object-cover rounded border border-gray-200"
      style={{
        width: '32px',
        height: '32px',
        objectFit: 'cover'
      }}
      onError={(e) => {
        // Fallback to icon if image fails to load
        const target = e.currentTarget as HTMLImageElement;
        target.style.display = 'none';
        const fallback = target.nextElementSibling as HTMLElement;
        if (fallback) {
          fallback.style.display = 'flex';
        }
      }}
    />
  );
};

interface JobCardData {
  workOrderNumber: string;
  date: string;
  styleNumber: string;
  itemName: string;
  description: string;
  size: string;
  cutSize: string;
  totalPieces: number;
  totalMaterialIssued: number;
  variants: Array<{
    id: number;
    variantName: string;
    sku: string;
    materialIssued: number;
    pieces: number;
    thumbnailUrl?: string;
  }>;
}

export function JobCardView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [workOrder, setWorkOrder] = useState<WorkOrder | null>(null);
  const [jobCardData, setJobCardData] = useState<JobCardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add ref to prevent duplicate calls
  const hasFetched = React.useRef(false);

  useEffect(() => {
    if (id) {
      fetchJobCardData();
    }
    
    // Cleanup function to reset ref when component unmounts
    return () => {
      hasFetched.current = false;
    };
  }, [id]);

  const fetchJobCardData = async () => {
    // Prevent duplicate calls
    if (hasFetched.current) {
      // console.log('Preventing duplicate API call');
      return;
    }
    hasFetched.current = true;

    try {
      setLoading(true);
      setError(null);
      
      // console.log('Starting JobCard data fetch...');
      
      // Fetch work order data
      const workOrderResponse = await workOrderApiService.getWorkOrderById(id!);
      if (!workOrderResponse.success || !workOrderResponse.data) {
        throw new Error('Work order not found');
      }
      
      const wo = workOrderResponse.data;
      setWorkOrder(wo);

      // Fetch endgood details
      let endgood: Endgood | null = null;
      if (wo.endgood?.id) {
        try {
          endgood = await endgoodApiService.getEndgoodById(wo.endgood.id.toString());
        } catch (error) {
          // console.error('Error fetching endgood:', error);
        }
      }

      // Skip material issues API call - we can calculate from variant breakdowns
      // console.log('Skipping material issues API call - using variant breakdown data instead');

      // Fetch variant breakdowns
      let variantBreakdowns: any[] = [];
      if (wo.endgood?.id) {
        const variantResponse = await workOrderVariantBreakdownApiService.getWorkOrderVariantBreakdownsByEndgood(wo.endgood.id);
        variantBreakdowns = variantResponse.success ? variantResponse.data || [] : [];
      }

      // Load all variant images in one batch to avoid multiple API calls
      let variantImages: { [key: string]: string } = {};
      if (wo.endgood?.id && variantBreakdowns.length > 0) {
        try {
          const files = await fileUploadApiService.getFilesByEndgood(wo.endgood.id);
          
          // Create a map of variant names to image URLs
          variantBreakdowns.forEach(variant => {
            const variantName = variant.endgoodVariantColor;
            if (variantName) {
              const matchingFile = files.find(file => {
                const fileName = file.originalName || file.originalFileName || file.fileName || '';
                return fileName.toLowerCase().includes(variantName.toLowerCase());
              });
              
              if (matchingFile?.fileUrl) {
                variantImages[variantName] = matchingFile.fileUrl;
              }
            }
          });
        } catch (error) {
          // console.error('Error loading variant images:', error);
        }
      }

      // Transform data for job card
      // Calculate total material issued from variant breakdowns instead of separate API call
      const totalMaterialIssued = variantBreakdowns.reduce((sum, variant) => sum + (variant.materialIssued || 0), 0);
      
      const variants = variantBreakdowns.map(variant => ({
        id: variant.id,
        variantName: variant.endgoodVariantColor || 'N/A',
        sku: variant.endgoodVariantSku || endgood?.sku || 'N/A',
        materialIssued: variant.materialIssued || 0,
        pieces: variant.quantity || 0,
        thumbnailUrl: variantImages[variant.endgoodVariantColor] || undefined
      }));

      const jobCard: JobCardData = {
        workOrderNumber: wo.workOrderNumber || 'N/A',
        date: new Date().toLocaleDateString(),
        styleNumber: endgood?.partNumber || 'N/A',
        itemName: endgood?.endgoodName || 'N/A',
        description: endgood?.description || 'N/A',
        size: endgood?.endgoodName || 'N/A',
        cutSize: '110" x 110"', // Default cut size
        totalPieces: wo.quantity || 0,
        totalMaterialIssued: totalMaterialIssued,
        variants: variants
      };

      setJobCardData(jobCard);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch job card data';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/work-orders');
  };

  const handlePrint = () => {
    window.print();
    toast.success('Job card sent to printer');
  };

  const handleDownloadPDF = () => {
    toast.success('Job card PDF downloaded successfully');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading job card...</span>
      </div>
    );
  }

  if (error || !jobCardData) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error Loading Job Card</h2>
          <p className="text-gray-600 mb-4">{error || 'Job card data not found'}</p>
          <Button onClick={handleBack} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Work Orders
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 print:p-0 max-w-7xl">
      {/* Header Actions - Hidden when printing */}
      <div className="flex justify-between items-center mb-6 print:hidden">
        <div>
          <h1 className="text-3xl font-bold text-primary-800 mb-2">Job Card View</h1>
          <p className="text-gray-600">Static view of production job card</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={handleDownloadPDF}
            className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white"
          >
            <Download className="h-4 w-4 mr-2" />
            Download as PDF
          </Button>
          <Button 
            onClick={handlePrint}
            variant="outline"
            className="border-primary-200 text-primary-700 hover:bg-primary-50"
          >
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
        </div>
      </div>

      {/* Job Card */}
      <Card className="border-primary-200 shadow-2xl bg-white print:shadow-none print:border-none">
        <CardContent className="p-0">
          <div className="bg-white text-black print:p-0 p-8" style={{ fontFamily: 'Arial, sans-serif' }}>
            
            {/* Job Card Header */}
            <div className="bg-gradient-to-r from-primary-50 to-primary-100 p-6 rounded-lg mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="h-8 w-8 text-primary-600" />
                  <div>
                    <h2 className="text-2xl font-bold text-primary-800">JOB CARD</h2>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-semibold text-gray-800">Manufacturing Job Card</div>
                  <div className="text-sm text-gray-600">Production Department</div>
                </div>
              </div>
            </div>

            {/* Work Order Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              {/* Left Column */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Hash className="h-5 w-5 text-primary-600" />
                  <div>
                    <span className="text-gray-600">Work Order No.:</span>
                    <div className="text-lg font-bold text-primary-700">#{jobCardData.workOrderNumber}</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <Package className="h-5 w-5 text-primary-600" />
                  <div>
                    <span className="text-gray-600">Style No. (Part No.):</span>
                    <div className="text-lg font-bold text-gray-800">{jobCardData.styleNumber}</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <Ruler className="h-5 w-5 text-primary-600" />
                  <div>
                    <span className="text-gray-600">Size (Description):</span>
                    <div className="text-lg font-bold text-gray-800">{jobCardData.description}</div>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-primary-600" />
                  <div>
                    <span className="text-gray-600">Date:</span>
                    <div className="text-lg font-bold text-gray-800">{jobCardData.date}</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <Package className="h-5 w-5 text-primary-600" />
                  <div>
                    <span className="text-gray-600">Item (Item Name):</span>
                    <div className="text-lg font-bold text-gray-800">{jobCardData.itemName}</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <Scissors className="h-5 w-5 text-primary-600" />
                  <div>
                    <span className="text-gray-600">Cut Size:</span>
                    <div className="text-lg font-bold text-gray-800">{jobCardData.cutSize}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Summary Boxes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                <div className="text-blue-600 text-sm font-medium mb-2">Total No of Pieces</div>
                <div className="text-4xl font-bold text-blue-800">{jobCardData.totalPieces}</div>
              </div>
              
              <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                <div className="text-green-600 text-sm font-medium mb-2">Total Mt Issued</div>
                <div className="text-4xl font-bold text-green-800">{jobCardData.totalMaterialIssued}</div>
              </div>
            </div>

            {/* Production Tasks Table */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Package className="h-6 w-6 text-primary-600" />
                <h3 className="text-xl font-bold text-primary-800">Production Tasks</h3>
              </div>
              
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-primary-100">
                      <TableHead className="font-bold text-gray-800 border-r border-gray-300 text-center">Variant</TableHead>
                      <TableHead className="font-bold text-gray-800 border-r border-gray-300">Variant Names</TableHead>
                      <TableHead className="font-bold text-gray-800 border-r border-gray-300">SKU</TableHead>
                      <TableHead className="font-bold text-gray-800 border-r border-gray-300 text-center">Mt Issued</TableHead>
                      <TableHead className="font-bold text-gray-800 text-center">Pieces</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {jobCardData.variants.map((variant, index) => (
                      <TableRow key={variant.id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <TableCell className="border-r border-gray-200 text-center">
                          <div className="flex justify-center">
                            <VariantThumbnail 
                              variantName={variant.variantName} 
                              thumbnailUrl={variant.thumbnailUrl}
                            />
                          </div>
                        </TableCell>
                        <TableCell className="border-r border-gray-200 font-medium">{variant.variantName}</TableCell>
                        <TableCell className="border-r border-gray-200 font-medium">{variant.sku}</TableCell>
                        <TableCell className="border-r border-gray-200 text-center">
                          <span className="text-green-600 font-bold">{variant.materialIssued}</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="text-purple-600 font-bold">{variant.pieces}</span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Footer Signatures */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-8 border-t-2 border-gray-300">
              <div className="text-center">
                <div className="border-t border-gray-400 pt-2 mt-16 mx-4">
                  <div className="font-bold text-gray-800">Prepared By</div>
                  <div className="text-sm text-gray-600 mt-2">_________________</div>
                </div>
              </div>
              
              <div className="text-center">
                <div className="border-t border-gray-400 pt-2 mt-16 mx-4">
                  <div className="font-bold text-gray-800">Checked By</div>
                  <div className="text-sm text-gray-600 mt-2">_________________</div>
                </div>
              </div>
              
              <div className="text-center">
                <div className="border-t border-gray-400 pt-2 mt-16 mx-4">
                  <div className="font-bold text-gray-800">Approved By</div>
                  <div className="text-sm text-gray-600 mt-2">_________________</div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="text-center text-xs text-gray-500 mt-8 pt-4 border-t border-gray-300">
              Generated on: {new Date().toLocaleString()}
            </div>
          </div>
        </CardContent>
      </Card>

    </div>
  );
}
